import { useCallback, useEffect, useState } from 'react';

/**
 * Persists the visitor's chosen country (ISO alpha-2) in localStorage so
 * search bars and provider lookups default to the right market on every
 * future visit.  Updates are broadcast via a custom event so any other
 * component listening with this hook re-renders immediately.
 */
const STORAGE_KEY = 'afroespace.country';
const EVENT = 'afroespace:country-changed';

export interface SelectedCountry {
  /** Current ISO-2 code (never empty — falls back to `defaultCode`). */
  countryCode: string;
  /** True when the visitor has explicitly chosen, false on first visit. */
  hasUserChosen: boolean;
  /** Persist a new selection and notify the rest of the app. */
  setCountryCode: (code: string) => void;
  /** Reset to "no choice yet" — used by sign-out / "wrong country?" flows. */
  clearCountry: () => void;
}

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function useSelectedCountry(defaultCode = 'DE'): SelectedCountry {
  const [stored, setStored] = useState<string | null>(() => readStored());

  useEffect(() => {
    const onCustom = (e: Event) => {
      setStored((e as CustomEvent<string | null>).detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setStored(e.newValue);
    };
    window.addEventListener(EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setCountryCode = useCallback((code: string) => {
    const normalised = code.toUpperCase();
    try {
      window.localStorage.setItem(STORAGE_KEY, normalised);
    } catch {
      // Private-mode browsers may block writes — fall back to in-memory.
    }
    window.dispatchEvent(
      new CustomEvent<string | null>(EVENT, { detail: normalised }),
    );
    setStored(normalised);
  }, []);

  const clearCountry = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* see above */
    }
    window.dispatchEvent(
      new CustomEvent<string | null>(EVENT, { detail: null }),
    );
    setStored(null);
  }, []);

  return {
    countryCode: stored ?? defaultCode,
    hasUserChosen: stored !== null,
    setCountryCode,
    clearCountry,
  };
}
